import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DataGridPro,
  GridCallbackDetails,
  GridCellParams,
  GridEventListener,
  GridRowClassNameParams,
  GridRowModesModel,
  GridRowProps,
  GridRowsProp,
  GridSortModel,
  GridColDef,
  GridProSlotsComponent,
  GridRowId,
  GridAutosizeOptions,
  GridRowSelectionModel,
  GridApiPro,
  GridState,
} from '@mui/x-data-grid-pro';
import { SUMROWID } from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataTable/Consts/BusinessDataTableConsts';
import { TSelectedDetail } from '../../Types/MasterDetailTypes';
import { definedSelectedId } from '../../Components/MasterDetail/MasterDetailUtils';
import { EDDataGridClasses, getFirstEditableCell } from './EdDataGridUtils';
import { equals } from 'ramda';
import { useAtom } from 'jotai';
import { GlobalColumnSettings } from '../GlobalStateHandler/GlobalStateHandler';
import { EDAvenyDataGridProgress } from '../ProgressIndicators/AvenyProgress/components/EDAvenyDataGridProgress';
import { FLOATING_MENU_CLEARANCE } from '../../Components/FloatingBottomArea/FloatingMenu';

export type RowComponentProps = React.HTMLAttributes<HTMLDivElement> &
  GridRowProps;

/** Typ för en custom komponent för rader, extraprops behöver skickas in via rowProps */
export type RowComponentType<extraprops = {}> = React.FunctionComponent<
  React.PropsWithChildren<RowComponentProps & extraprops>
>;
interface IProps {
  columns: GridColDef[];
  rows: GridRowsProp;
  loading?: boolean;
  onCellClick?: GridEventListener<'cellClick'>;
  onCellDoubleClick?: GridEventListener<'cellDoubleClick'>;
  onCellKeyDown?: GridEventListener<'cellKeyDown'>;
  onRowClick?: GridEventListener<'rowClick'>;
  selectionGrid?: boolean;
  selectedId?: TSelectedDetail;
  emptyView?: React.FunctionComponent<React.PropsWithChildren<unknown>>;
  /** Komponent för att visa rader, kan t.ex. användas för att wrappa den vanliga komponenten med ett context */
  RowComponent?: RowComponentType<any>;
  ColumnMenu?: React.FunctionComponent<React.PropsWithChildren<unknown>>;
  ColumnMenuIcon?: React.FunctionComponent<React.PropsWithChildren<unknown>>;
  disableColumnMenu?: boolean;
  disableColumnReorder?: boolean;
  disableMultipleColumnsSorting?: boolean;
  rowClassName?: string;
  onColumnOrderChange?: GridEventListener<'columnOrderChange'>;
  sortModel?: GridSortModel;
  onSortModelChange?: (
    model: GridSortModel,
    details: GridCallbackDetails<any>
  ) => void;
  disabled?: boolean;
  columnMenuProps?: any;
  emptyViewProps?: any;
  rowProps?: any;
  rowHeight?: number;
  autoHeight?: boolean;
  apiRef?: React.RefObject<GridApiPro | null>;
  columnBorders?: boolean;
  testId?: string;
  /** id som inställningar som t.ex. aktuell kolumnbredd sparas under */
  settingsId?: string;
  isCellEditable?: (params: GridCellParams<any, any, any>) => boolean;
  editable?: boolean;
  editMode?: boolean;
  onRowEditStart?: GridEventListener<'rowEditStart'>;
  onRowEditStop?: GridEventListener<'rowEditStop'>;
  cellTabIndex?: 0 | -1;
  rowModesModel?: GridRowModesModel;
  onRowModesModelChange?: (
    rowModesModel: GridRowModesModel,
    details: GridCallbackDetails<any>
  ) => void;
  onBlur?: () => void;
  getSelectedRowClassName?: any;
  getRowClassName?: (params: GridRowClassNameParams<any>) => string;
  onColumnWidthChange?: GridEventListener<'columnWidthChange'>;
  processRowUpdate?:
    | ((
        newRow: any,
        oldRow: any,
        params: {
          rowId: GridRowId;
        }
      ) => any)
    | undefined;
  onProcessRowUpdateError?: ((error: any) => void) | undefined;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  floatingMenuBottomSpacing?: boolean;
}
const autoSizeOptions: GridAutosizeOptions = {
  includeOutliers: true,
};
const EDDataGrid: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { onBlur, onColumnWidthChange } = props;
  const [tableHasBeenFocused, setTableHasBeenFocused] = useState(false);
  const [columnSetting, setColumnSetting] = useAtom(
    GlobalColumnSettings(props.settingsId ?? 'default')
  );
  const components: Partial<GridProSlotsComponent> = useMemo(
    () =>
      ({
        noRowsOverlay: props.emptyView,
        loadingOverlay: EDAvenyDataGridProgress,
        row: props.RowComponent,
        columnMenu: props.ColumnMenu,
        columnMenuIcon: props.ColumnMenuIcon,
      } as Partial<GridProSlotsComponent>),
    [
      props.ColumnMenu,
      props.ColumnMenuIcon,
      props.emptyView,
      props.RowComponent,
    ]
  );

  //komplicerat hantering för att se om fokus lämnar tabellen utan att menyer som renderats via portaler ska räknas som utanför som det blir om man tittar på element.contains
  const focusCapturedRef = useRef<boolean>(false); //om ett focusIn fångats av div runt tabellen (ett react-event så även focus på element som renderats av portaler)
  const focusInsideRef = useRef<boolean>(false); //om senaste focus var inne i tabellen
  // För att hålla reda på när raderna har uppdaterats internt i gridden
  const prevGridApiRowIdsRef = useRef<GridRowId[]>(undefined);

  // focusIn som registreras på window
  // focus har flyttats utanför gridden om den varit fokuserad och något får fokus utan att focusCaptured satts innan
  const onFocusIn = useCallback(
    (event: Event) => {
      if (!focusCapturedRef.current) {
        if (focusInsideRef.current) {
          focusInsideRef.current = false;
          onBlur?.();
        }
      } else {
        focusInsideRef.current = true;
      }
      focusCapturedRef.current = false;
    },
    [onBlur]
  );

  // focusOut som registreras på window
  // focus har flyttats utanför gridden om fokus flyttas från ett element utan att flyttas till något annat element
  const onFocusOut = useCallback(
    (event: Event) => {
      const focusEvent = event as FocusEvent;
      if (!focusEvent.relatedTarget && focusInsideRef.current) {
        focusInsideRef.current = false;
        onBlur?.();
      }
    },
    [onBlur]
  );

  //om onBlur är satt så registrerar vi event-handler för focusin och focusout på window
  useEffect(() => {
    if (props.onBlur) {
      window?.addEventListener('focusin', onFocusIn);
      window?.addEventListener('focusout', onFocusOut);
      return () => {
        window?.removeEventListener('focusin', onFocusIn);
        document?.removeEventListener('focusout', onFocusOut);
      };
    } else {
      return undefined;
    }
  }, [onFocusIn, onFocusOut, props.onBlur]);

  const handleColumnWidthChange: GridEventListener<'columnWidthChange'> =
    useCallback(
      (params, event, details) => {
        setColumnSetting(columnSetting => ({
          ...columnSetting,
          [params.colDef.field]: { width: params.width },
        }));
        onColumnWidthChange?.(params, event, details);
      },
      [onColumnWidthChange, setColumnSetting]
    );
  const columns = useMemo(() => {
    return props.columns.map(col => {
      const columnSettingForCol = columnSetting[col.field];
      return {
        ...col,
        width: columnSettingForCol?.width ?? col.width,
      };
    });
  }, [columnSetting, props.columns]);

  const onStateChange = useCallback<GridEventListener<'stateChange'>>(
    (newState: GridState) => {
      if (!equals(prevGridApiRowIdsRef.current, newState?.rows.dataRowIds)) {
        prevGridApiRowIdsRef.current = newState?.rows.dataRowIds;

        const firstEditableCell = props.apiRef?.current
          ? getFirstEditableCell(props.apiRef?.current)
          : undefined;
        if (
          props.apiRef?.current &&
          firstEditableCell &&
          !tableHasBeenFocused
        ) {
          props.apiRef?.current.setState(prevState => {
            return {
              ...prevState,
              tabIndex: {
                ...prevState.tabIndex,
                cell: firstEditableCell,
              },
            };
          });
        }
      }
    },
    [props.apiRef, tableHasBeenFocused]
  );
  const rowSelection: GridRowSelectionModel = useMemo(() => {
    if (props.selectedId) {
      return {
        type: 'include',
        ids: new Set([definedSelectedId(props.selectedId)]),
      };
    } else {
      //return empty Set
      return { type: 'include', ids: new Set() };
    }
  }, [props.selectedId]);

  return (
    <div
      ref={props.containerRef}
      style={{ height: '100%', minHeight: '10px' }}
      onFocus={event => {
        focusCapturedRef.current = true;
        setTableHasBeenFocused(true);
      }}
    >
      <DataGridPro
        onRowClick={props.onRowClick}
        onStateChange={onStateChange}
        sx={[
          Boolean(props.floatingMenuBottomSpacing) && {
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              paddingBottom: FLOATING_MENU_CLEARANCE,
            },
          },
          {
            '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
              paddingLeft: 2,
              paddingRight: 1,
            },
            '& .MuiDataGrid-columnHeader:last-child, .MuiDataGrid-cell:last-child':
              {
                paddingLeft: 2,
                paddingRight: 2,
              },
            '& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator--sideRight.MuiDataGrid-columnSeparator--resizable':
              {
                visibility: 'visible',
              },
            '& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator--sideRight':
              {
                visibility: 'hidden',
              },
            '& .MuiDataGrid-columnHeader .MuiBadge-badge': {
              backgroundColor: 'primary.main',
              color: 'common.white',
              height: 14,
              width: 14,
              minWidth: 14,
              transform: 'translate(5px, 15px)',
            },
            //Visa komponenter i samma ordning för högerställda kolumner. Sorteringspilen är t.ex. till höger om rubriken
            '.MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
              {
                justifyContent: 'flex-end',
                flexDirection: 'row',
              },
            border: 'none',
          },
          props.onRowClick
            ? {
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: theme =>
                    `${theme.customProperties.DataGrid.rowHoverBackgroundColor}`,
                },
              }
            : {
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'inherit',
                },
              },
          Boolean(props.editable) && {
            [`& .MuiDataGrid-cell`]: {
              padding: 0,
              zIndex: 0,
            },
            [`& .${EDDataGridClasses.cellReadonly} .MuiDataGrid-cellContent`]: {
              cursor: 'text',
            },
            [`& .${EDDataGridClasses.cellReadonly}`]: {
              cursor: 'default',
            },
            [`&& .${EDDataGridClasses.cellReadonly}`]: {
              padding: theme => `0 ${theme.spacing(1)} 0 ${theme.spacing(0.5)}`,
            },
          },
          Boolean(props.editMode) && {
            [`& .${EDDataGridClasses.cellDisabled}:not(.${EDDataGridClasses.cellReadonly})`]:
              {
                backgroundColor: theme =>
                  theme.palette.action.disabledBackground,
              },
            [`&& .MuiDataGrid-row--editing .${EDDataGridClasses.cellDisabled}:not(.${EDDataGridClasses.cellReadonly})`]:
              {
                backgroundColor: theme =>
                  theme.palette.action.disabledBackground,
              },
          },
          {
            [`& .${EDDataGridClasses.summaryRow}`]: {
              cursor: 'default',
              fontWeight: '700',
            },
            [`& .${EDDataGridClasses.summaryRow} .MuiDataGrid-cell`]: {
              borderRight: 'none',
            },
            '& .MuiDataGrid-row Mui-selected': {
              backgroundColor: theme =>
                `${theme.customProperties.selectFocusBackgroundColor}`,
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: theme =>
                `${theme.customProperties.selectFocusBackgroundColor}`,
              filter: 'brightness(95%)',
            },
            '& .MuiDataGrid-row--editing': {
              boxShadow: 'none',
            },
            '& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
              backgroundColor: theme =>
                `${theme.customProperties.DataGrid.activeRowBackgroundColor}`,
            },
            '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': { paddingLeft: 0 },
            '& .MuiDataGrid-cell--editing:focus-within': {
              backgroundColor: theme =>
                `${theme.customProperties.DataGrid.activeCellBackgroundColor}`,
            },
          },
          !props.editMode && {
            [`& .MuiDataGrid-cell:focus`]: {
              outline: 'none',
            },
            [`& .MuiDataGrid-cell:focus-within`]: {
              outline: 'none',
            },
          },
          {
            [`& .${EDDataGridClasses.linkText}`]: {
              fontWeight: 'bold',
              color: 'text.primary',
            },
          },
          !props.disabled && {
            [`& .MuiDataGrid-row:hover .${EDDataGridClasses.functionMenu}`]: {
              opacity: 1,
            },
          },
          {
            [`& .${EDDataGridClasses.functionMenu} `]: {
              opacity: 0,
              transition: 'opacity 100ms ease',
            },
          },
          !!props.columnBorders && {
            '& .MuiDataGrid-cell': {
              borderRight: theme =>
                `1px solid ${theme.customProperties.DataGrid.lightGridBorderColor}`,
            },
            '& .MuiDataGrid-cell:nth-last-of-type(2)': {
              borderRight: 'none',
            },
            '& .MuiDataGrid-cell:last-child': {
              borderRight: `none`,
            },
          },
          {
            [`& .${EDDataGridClasses.importDialogCellInvalidValue}`]: {
              color: theme => `${theme.palette.warning.main}`,
              fontWeight: theme => theme.typography.fontWeightMedium,
            },
          },
        ]}
        apiRef={props.apiRef}
        autoHeight={props.autoHeight}
        columns={columns}
        rows={props.rows}
        loading={props.loading}
        rowSelectionModel={rowSelection}
        onCellClick={props.onRowClick ? undefined : props.onCellClick}
        onCellDoubleClick={props.onCellDoubleClick}
        onCellKeyDown={props.onCellKeyDown}
        disableColumnMenu={props.disableColumnMenu}
        slots={components}
        hideFooter
        rowHeight={props.rowHeight ?? 42}
        columnHeaderHeight={42}
        disableRowSelectionOnClick={!props.selectionGrid}
        disableMultipleRowSelection={props.selectionGrid ?? false}
        disableColumnReorder={props.disableColumnReorder}
        disableMultipleColumnsSorting={props.disableMultipleColumnsSorting}
        onColumnOrderChange={props.onColumnOrderChange}
        sortModel={props.sortModel}
        sortingMode="server"
        onSortModelChange={props.onSortModelChange}
        editMode="row"
        slotProps={{
          noRowsOverlay: props.emptyViewProps,
          row: props.rowProps,
          cell:
            props.cellTabIndex != null
              ? { tabIndex: props.cellTabIndex }
              : undefined,
          columnMenu: props.columnMenuProps,
          root: {
            'data-testid': props.testId,
          },
        }}
        getRowClassName={params => {
          if (params.id === SUMROWID) {
            return EDDataGridClasses.summaryRow;
          }
          if (props.getRowClassName != null) {
            return props.getRowClassName(params);
          }
          return '';
        }}
        isCellEditable={props.isCellEditable}
        onRowEditStart={props.onRowEditStart}
        onRowEditStop={props.onRowEditStop}
        columnBufferPx={500}
        onRowModesModelChange={props.onRowModesModelChange}
        rowModesModel={props.rowModesModel}
        onColumnWidthChange={handleColumnWidthChange}
        autosizeOptions={autoSizeOptions}
        processRowUpdate={props.processRowUpdate}
        onProcessRowUpdateError={props.onProcessRowUpdateError}
      />
    </div>
  );
};

export default EDDataGrid;
